/**
 * The questions the owners, officers, subsidiaries and state registrations tables ask about a row
 * before they draw it or write it: does the row wait for a person, which value does each cell show
 * once the choices made in its cells' ValuePicker are counted, and what does the row's Confirm
 * review button send.
 *
 * A row of those tables is reviewed as a whole. A choice made in a cell's ValuePicker is kept on
 * the page, one FieldDecision per cell key, until Confirm review sends every choice of the row
 * (reviewListRow in useProfileFieldWrites.tsx). A cell nobody made a choice for is sent with its
 * newest waiting value, which is the one ValuePicker preselects, so each cell shows the value
 * Confirm review would write: the choice, else the newest waiting value, else the approved value.
 */

import {
  TAX_PROFILE_LIST_COLUMNS,
  TaxProfileListColumnDefinition,
  taxProfileListColumnByKey,
} from '@shared/constants/taxProfileFields';
import type {
  TaxProfileApprovedValue,
  TaxProfileFieldValue,
  TaxProfileListRow,
  TaxProfileValueSource,
} from '@shared/types/taxProfile';
import { fieldNeedsReview, newestWaiting } from '@/pages/TaxProfile/fieldState';
import type {
  FieldWriteTarget,
  TypedCellWrite,
} from '@/pages/TaxProfile/useProfileFieldWrites';
import type { FieldDecision } from '@/pages/TaxProfile/components/ValuePicker';

/** The choices made in one row's cells, keyed by the cell's list column key. */
export type RowDecisions = Record<string, FieldDecision>;

/**
 * The choices made in one row's cells as ListTable keeps them, keyed by decisionKey below: the
 * column key, with the tax year added for a per-year column.
 */
export type KeptRowDecisions = Record<string, FieldDecision>;

/**
 * The key a row's choice for one column is kept under: the column key, and for a per-year column
 * (`yearly` in TAX_PROFILE_LIST_COLUMNS: owner_ownership_pct, owner_profit_allocation_pct,
 * owner_capital_pct, owner_beginning_capital_account) the tax year of the cell as well, as noticeKey
 * in useProfileFieldWrites.tsx files a notice.
 *
 * Keyed by the column key alone, a choice made for 2024 was shown in the 2023 cell after the year
 * switch, added into the 2023 Total, and sent under 2023 by Confirm review.
 */
export const decisionKey = (key: string, taxYear: number | null): string =>
  taxProfileListColumnByKey(key)?.yearly ? `${key}:${taxYear ?? ''}` : key;

/**
 * The choices of one row for the year the page shows, keyed by column key, which is what every
 * reader of a row's choices takes (rowCellValue, rowReviewWrites, shareTotal).
 *
 * Expects the row, the choices ListTable keeps for it (keyed by decisionKey) and the year the page
 * shows. A per-year cell is looked up under the cell's own tax year, else the year shown, which is
 * the year listCellTarget writes it under.
 */
export const decisionsForYear = (
  row: TaxProfileListRow,
  kept: KeptRowDecisions | undefined,
  shownTaxYear: number | null
): RowDecisions => {
  if (!kept) return {};
  const shown: RowDecisions = {};
  TAX_PROFILE_LIST_COLUMNS.forEach(({ key }) => {
    const decision =
      kept[decisionKey(key, row.cells[key]?.taxYear ?? shownTaxYear)];
    if (decision) shown[key] = decision;
  });
  return shown;
};

/** The list column for a key; every key a table draws is one of TAX_PROFILE_LIST_COLUMNS. */
export const listColumn = (key: string): TaxProfileListColumnDefinition => {
  const column = taxProfileListColumnByKey(key);
  if (!column) throw new Error(`No list column ${key}`);
  return column;
};

/**
 * True when the row waits for a person: nobody has approved its name yet (isNew), or one of its
 * cells has a candidate waiting (fieldNeedsReview). Such a row has a Finish review button; the
 * others write a typed value at once.
 */
export const rowIsWaiting = (row: TaxProfileListRow): boolean =>
  row.isNew || Object.values(row.cells).some(fieldNeedsReview);

/** The waiting group one of whose sources is the candidate `rowId` names. */
const groupOfRow = (cell: TaxProfileFieldValue, rowId: number) =>
  cell.waiting.find((group) =>
    group.sources.some((source) => source.rowId === rowId)
  ) ?? null;

/**
 * The value a cell shows: the typed value, the accepted candidate's value or the approved value
 * the choice names; with no choice, the newest waiting value, else the approved value; null when
 * the cell has none of those.
 */
export const rowCellValue = (
  cell: TaxProfileFieldValue | undefined,
  decision: FieldDecision | undefined
): string | number | boolean | null => {
  if (!cell) return decision?.kind === 'type' ? decision.value : null;
  if (decision?.kind === 'type') return decision.value;
  if (decision?.kind === 'keep') return cell.approved?.value ?? null;
  if (decision?.kind === 'accept') {
    const group = groupOfRow(cell, decision.rowId);
    if (group) return group.value;
  }
  const newest = newestWaiting(cell);
  if (newest) return newest.value;
  return cell.approved?.value ?? null;
};

/**
 * The source of the value rowCellValue returns, for the tax year it states: the accepted
 * candidate, the approved row, the newest waiting value's first source. Null for a typed value,
 * which is recorded for the year the page shows.
 */
export const rowCellSource = (
  cell: TaxProfileFieldValue | undefined,
  decision: FieldDecision | undefined
): TaxProfileValueSource | null => {
  if (!cell || decision?.kind === 'type') return null;
  if (decision?.kind === 'keep') return cell.approved;
  if (decision?.kind === 'accept') {
    const source = groupOfRow(cell, decision.rowId)?.sources.find(
      (candidate) => candidate.rowId === decision.rowId
    );
    if (source) return source;
  }
  return newestWaiting(cell)?.sources[0] ?? cell.approved;
};

/**
 * The write target of one cell: its column, its row, the tax year it is written under and the
 * scope the api sent with it.
 *
 * An owner's per-year column and officer_w2_compensation are written under the cell's year, or the
 * year the page shows where the api sent none (as targetFromField does for a catalog field); every
 * other column is written with no year.
 */
export const listCellTarget = (
  row: TaxProfileListRow,
  column: TaxProfileListColumnDefinition,
  cell: TaxProfileFieldValue,
  shownTaxYear: number | null
): FieldWriteTarget => ({
  definition: column,
  recordId: row.recordId,
  taxYear:
    column.yearly || column.taxYearColumn ? cell.taxYear ?? shownTaxYear : null,
  scope: {
    liveRowId: cell.liveRowId,
    consideredRowIds: cell.consideredRowIds,
  },
});

/**
 * What a Confirm review sends for one cell or one catalog field, given the choice made in its
 * ValuePicker:
 *  - a typed value: a typed write;
 *  - nothing waiting: nothing;
 *  - Current chosen on a value that has an approved one: an item keeping it (no acceptRowId);
 *  - otherwise an item accepting the candidate the person chose, else the newest one, which is the
 *    one ValuePicker preselects (acceptRowId).
 *
 * `target` is the value's write target with no acceptRowId (listCellTarget, targetFromField less
 * its acceptRowId). The person window (rowReviewWrites below) and a document's summary screen
 * (ReviewModal) both send what this returns.
 */
export const cellReviewWrite = (
  target: FieldWriteTarget,
  cell: TaxProfileFieldValue,
  decision: FieldDecision | undefined
): { decided: FieldWriteTarget } | { typed: TypedCellWrite } | null => {
  if (decision?.kind === 'type') {
    return { typed: { target, value: decision.value } };
  }
  if (cell.waiting.length === 0) return null;
  if (decision?.kind === 'keep' && cell.approved) return { decided: target };
  const acceptRowId =
    decision?.kind === 'accept' && groupOfRow(cell, decision.rowId)
      ? decision.rowId
      : cell.waiting[0].sources[0]?.rowId;
  if (acceptRowId == null) return null;
  return { decided: { ...target, acceptRowId } };
};

/**
 * What a row's Confirm review sends: cellReviewWrite for every cell of the row, the decided cells
 * as items of the review route and the typed ones as writes of the edit route.
 */
export const rowReviewWrites = (
  row: TaxProfileListRow,
  decisions: RowDecisions,
  shownTaxYear: number | null
): { decided: FieldWriteTarget[]; typed: TypedCellWrite[] } => {
  const decided: FieldWriteTarget[] = [];
  const typed: TypedCellWrite[] = [];
  Object.entries(row.cells).forEach(([key, cell]) => {
    const column = taxProfileListColumnByKey(key);
    if (!column) return;
    const write = cellReviewWrite(
      listCellTarget(row, column, cell, shownTaxYear),
      cell,
      decisions[key]
    );
    if (!write) return;
    if ('typed' in write) typed.push(write.typed);
    else decided.push(write.decided);
  });
  return { decided, typed };
};

/**
 * Who approved the row and when: the name cell's approved value, else the cell approved last; null
 * when no cell of the row is approved.
 */
export const rowApproval = (
  row: TaxProfileListRow,
  nameKey: string
): TaxProfileApprovedValue | null => {
  const named = row.cells[nameKey]?.approved;
  if (named) return named;
  return Object.values(row.cells)
    .map((cell) => cell.approved)
    .filter((approved): approved is TaxProfileApprovedValue => !!approved)
    .reduce<TaxProfileApprovedValue | null>(
      (latest, approved) =>
        !latest || (approved.approvedAt ?? '') > (latest.approvedAt ?? '')
          ? approved
          : latest,
      null
    );
};

/**
 * The sum of one percent column over the rows, from the value each cell shows (rowCellValue), and
 * whether it is more than half a point away from 100. Null when no row has a number in the column.
 */
export const shareTotal = (
  rows: TaxProfileListRow[],
  key: string,
  decisionsOf: (row: TaxProfileListRow) => RowDecisions
): { total: number; isOff: boolean } | null => {
  const numbers = rows
    .map((row) => rowCellValue(row.cells[key], decisionsOf(row)[key]))
    .filter((value) => value !== null && value !== '')
    .map(Number)
    .filter((value) => !Number.isNaN(value));
  if (numbers.length === 0) return null;
  // Rounded to four decimals so that 33.33 + 33.33 + 33.34 prints 100, not 99.99999999999999.
  const total =
    Math.round(numbers.reduce((sum, value) => sum + value, 0) * 10000) / 10000;
  return { total, isOff: Math.abs(total - 100) > 0.5 };
};
